import React from 'react';
import { motion } from 'framer-motion';
import { Sign } from '../../data/signs';
import { ParticleField } from '../shared/ParticleField';
import { startCheckout } from '../../lib/metaPixel';
interface SignHeroProps {
  sign: Sign;
}
export function SignHero({
  sign
}: SignHeroProps) {
  const scrollToOffers = () => {
    const el = document.getElementById('sign-offers');
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    } else {
      startCheckout(sign.name, sign.checkoutUrl, 12.69);
    }
  };
  return <section className="relative w-full min-h-[85vh] md:min-h-screen flex items-center justify-center overflow-hidden py-16 md:py-24" style={{
    background: `linear-gradient(135deg, ${sign.colors.primary}20 0%, ${sign.colors.secondary}10 50%, #050508 100%)`
  }} data-id="element-925">
      <ParticleField type={sign.particleType} color={sign.colors.primary} density={50} data-id="element-926" />

      {/* Background image with overlay */}
      <div className="absolute inset-0" data-id="element-927">
        <img src={sign.imageUrl} alt={sign.entidad} className="w-full h-full object-cover opacity-55" loading="eager" data-id="element-928" />
        <div className="absolute inset-0" style={{
        background: `linear-gradient(180deg, rgba(5,5,8,0.55) 0%, ${sign.colors.primary}25 50%, rgba(5,5,8,0.92) 100%)`
      }} data-id="element-929" />
        <div className="absolute inset-0" style={{
        background: `radial-gradient(circle at center, transparent 0%, ${sign.colors.primary}15 40%, rgba(5,5,8,0.8) 100%)`
      }} data-id="element-930" />
      </div>

      <div className="relative z-10 text-center px-4 md:px-6 max-w-5xl mt-10 md:mt-0" data-id="element-931">
        <motion.h1 initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.2
      }} className="text-[34px] leading-[1.08] md:text-8xl font-serif font-bold mb-3 md:mb-4 tracking-wide" style={{
        color: '#FFFFFF',
        textShadow: `0 0 60px ${sign.colors.primary}80, 0 4px 30px rgba(0,0,0,0.9)`
      }} data-id="element-932">
          {sign.name}
        </motion.h1>

        {/* Sigil */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.5
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.5
      }} className="mb-4 md:mb-5" data-id="element-933">
          <span className="inline-block text-4xl md:text-6xl" style={{
          color: sign.colors.primary,
          textShadow: `0 0 30px ${sign.colors.primary}, 0 0 60px ${sign.colors.primary}80`
        }} data-id="element-934">
            {sign.symbol}
          </span>
        </motion.div>

        {/* Archetype label */}
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 0.7
      }} className="text-lg md:text-2xl font-serif text-gray-100 mb-6 md:mb-8 font-semibold" style={{
        textShadow: '0 2px 20px rgba(0,0,0,0.9)'
      }} data-id="element-935">
          {sign.arquetipo}
        </motion.p>

        {/* Psychological hero promise — replaces astrology framing */}
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.9
      }} className="text-[24px] leading-[1.18] md:text-3xl font-serif italic max-w-[94%] md:max-w-3xl mx-auto mb-5 md:mb-10 px-2" style={{
        color: '#F5F0E1',
        textShadow: `0 2px 24px rgba(0,0,0,0.95), 0 0 40px ${sign.colors.primary}40`
      }} data-id="element-936">
          {sign.heroPromise}
        </motion.p>

        {/* Mobile product clarity */}
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 1
      }} className="block md:hidden max-w-[92%] mx-auto mb-6 text-[15px] leading-[1.55] font-medium" style={{
        color: '#F5F0E1',
        textShadow: '0 2px 18px rgba(0,0,0,0.9)'
      }} data-id="element-937">
          Accede a un portal privado con documentos inmersivos, exploraciones
          simbólicas y contenido diseñado alrededor de tu Reino emocional.
        </motion.p>

        {/* Soft supporting phrase (kept for atmosphere) */}
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 1.1
      }} className="hidden md:block text-base md:text-lg italic mb-10 md:mb-12 opacity-90" style={{
        color: sign.colors.accent,
        textShadow: '0 2px 18px rgba(0,0,0,0.9)'
      }} data-id="element-938">
          "{sign.phrase}"
        </motion.p>

        <motion.button onClick={scrollToOffers} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 1.3
      }} className="inline-block w-full sm:w-auto px-8 md:px-10 py-4 md:py-5 text-[15px] md:text-lg font-bold rounded-xl transition-all duration-300 tracking-[0.18em] mt-2 md:mt-0" style={{
        backgroundColor: sign.colors.primary,
        color: '#050508',
        boxShadow: `0 0 40px ${sign.colors.primary}60`
      }} whileHover={{
        scale: 1.05,
        boxShadow: `0 0 60px ${sign.colors.primary}90`
      }} whileTap={{
        scale: 0.95
      }} data-id="element-939">
          Descubre Mi Reino
        </motion.button>

        <p className="block md:hidden mt-4 text-[13px] leading-relaxed text-[#E8D48B] opacity-90" data-id="element-940">
          Acceso inmediato al Portal privado
        </p>
      </div>
    </section>;
}